import { css, cx } from "@emotion/css";
import type { SimpleMenuItem } from "@octopusdeploy/design-system-components";
import { Collapse, Badge, Button, resetStyles, SimpleMenu, Tooltip, useContainsFocus, useIsHovered, useMenuState } from "@octopusdeploy/design-system-components";
import { ChevronDownIcon, ChevronUpIcon, EllipsisVerticalIcon, ExclamationTriangleIcon } from "@octopusdeploy/design-system-icons";
import { borderRadius, borderWidth, colorScales, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { LinkHref } from "@octopusdeploy/portal-routes";
import type { ReactNode } from "react";
import type React from "react";
import { useRef } from "react";
import { v4 } from "uuid";
import InternalLink from "~/components/Navigation/InternalLink";
import { RunInParallelIndicator } from "../RunInParallelIndicator";
import { ProcessSidebarStepMessages } from "./ProcessSidebarStepMessages";
export type ProcessSidebarStepData = ProcessSidebarStepActionData | ProcessSidebarStepContainerData;
export interface ProcessSidebarStepActionData {
    id: string;
    name: string;
    href: LinkHref;
    logo: {
        src: string;
    } | React.ReactElement;
    isSelected: boolean;
    isDisabled: boolean;
    errors: string[];
    warnings: ReactNode[];
    menuItems?: SimpleMenuItem[];
    isRunInParallelWithPreviousStep: boolean;
}
export interface ProcessSidebarStepContainerData extends ProcessSidebarStepActionData {
    containerLabel?: "Rolling" | "Template";
    containerSteps: ProcessSidebarStepData[];
}
export interface ProcessSidebarStepProps {
    stepData: ProcessSidebarStepData;
    isRootStep: boolean;
    onStepClick?: (stepData: ProcessSidebarStepData) => void;
    isCollapsed?: boolean;
    setIsCollapsed?: (isExpanded: boolean) => void;
}
export function ProcessSidebarStep({ stepData, isRootStep, onStepClick, isCollapsed = false, setIsCollapsed }: ProcessSidebarStepProps) {
    const { id, name, href, logo, isSelected, isDisabled, errors, warnings, menuItems } = stepData;
    const headerRef = useRef<HTMLDivElement>(null);
    const isHeaderHovered = useIsHovered(headerRef);
    const isHeaderContainsFocus = useContainsFocus(headerRef, { focusVisible: true });
    const hasErrors = errors.length > 0;
    const hasWarnings = warnings.length > 0;
    const isContainer = isProcessSidebarStepContainerData(stepData);
    const isCollapsible = isContainer && isCollapsed !== undefined && setIsCollapsed !== undefined;
    const childStepsContainerId = `child-steps-${v4()}`;
    const stateStyles = getStateStyles({ isSelected, isHovered: isHeaderHovered, isContainer, isRootStep, hasErrors, hasWarnings });
    const [onOverflowMenuButtonClick, overflowMenuState, overflowMenuButtonAriaAttributes] = useMenuState();
    const containerBadge = isContainer && stepData.containerLabel ? <Badge size="small" variant="neutral" label={stepData.containerLabel}/> : undefined;
    const showExpandCollapseButton = isCollapsible && (isHeaderHovered || isHeaderContainsFocus || overflowMenuState.isOpen);
    const showOverflowMenuButton = menuItems && menuItems.length > 0 && (isHeaderHovered || isHeaderContainsFocus || overflowMenuState.isOpen);
    const tooltipMessages = hasErrors ? errors : warnings;
    const errorOrWarningIcon = hasErrors ? (<ExclamationTriangleIcon size={24} color={themeTokens.color.processStep.icon.error.primary}/>) : hasWarnings ? (<ExclamationTriangleIcon size={24} color={themeTokens.color.processStep.icon.warning.primary}/>) : undefined;
    const ghostButtonStyles = cx(styles.ghostButton, {
        [styles.ghostButtonError]: hasErrors,
        [styles.ghostButtonWarning]: !hasErrors && hasWarnings,
    });
    return (<div className={cx(styles.root, stateStyles, { [styles.container]: isContainer, [styles.containerCollapsed]: isCollapsed })}>
            {stepData.isRunInParallelWithPreviousStep && <RunInParallelIndicator />}
            <div ref={headerRef} className={`${headerClassName} ${styles.header}`}>
                <MessagesTooltip messages={tooltipMessages}>
                    <InternalLink className={cx(styles.link, {
            [styles.linkWithLeftButton]: showExpandCollapseButton,
            [styles.linkWithRightButton]: showOverflowMenuButton,
        })} to={href} title={name} onClick={() => onStepClick?.(stepData)}>
                        {!showExpandCollapseButton && <div className={cx(styles.iconContainer, { [styles.iconDisabled]: isDisabled })}>{"src" in logo ? <img className={styles.icon} src={logo.src} alt=""/> : logo}</div>}
                        <span className={cx(styles.name, { [styles.nameDisabled]: isDisabled, [styles.nameSelected]: isSelected })}>{name}</span>
                        {!showOverflowMenuButton && (errorOrWarningIcon ? errorOrWarningIcon : containerBadge)}
                    </InternalLink>
                    {showExpandCollapseButton && (<div className={cx(ghostButtonStyles, styles.expandCollapseButton)}>
                            <Button icon={isCollapsed ? <ChevronDownIcon size={20}/> : <ChevronUpIcon size={20}/>} importance="ghost" size="small" onClick={() => setIsCollapsed(!isCollapsed)} accessibleName={`${isCollapsed ? "Expand" : "Collapse"} ${name} step`} aria-expanded={`${!isCollapsed}`} aria-controls={childStepsContainerId}/>
                        </div>)}
                    {showOverflowMenuButton && (<div className={cx(ghostButtonStyles, styles.overflowButton)}>
                            <Button icon={<EllipsisVerticalIcon size={20}/>} importance="ghost" size="small" onClick={onOverflowMenuButtonClick} accessibleName={`${overflowMenuState.isOpen ? "Hide" : "Show"} ${name} step menu`} {...overflowMenuButtonAriaAttributes}/>
                            <SimpleMenu accessibleName={`${name} step menu`} items={menuItems} menuState={overflowMenuState}/>
                        </div>)}
                </MessagesTooltip>
            </div>
            {isContainer && (<Collapse id={childStepsContainerId} in={!isCollapsed} timeout={200}>
                    <ol className={styles.childSteps}>
                        {stepData.containerSteps.map((stepData) => (<li key={stepData.id}>
                                <ProcessSidebarStep stepData={stepData} isRootStep={false} onStepClick={onStepClick}/>
                            </li>))}
                    </ol>
                </Collapse>)}
        </div>);
}
export function isProcessSidebarStepContainerData(step: ProcessSidebarStepData): step is ProcessSidebarStepContainerData {
    return "containerSteps" in step;
}
function getStateStyles({ isSelected, isHovered, isContainer, isRootStep, hasErrors, hasWarnings }: {
    isSelected: boolean;
    isHovered: boolean;
    isContainer: boolean;
    isRootStep: boolean;
    hasErrors: boolean;
    hasWarnings: boolean;
}) {
    if (hasErrors) {
        return {
            [errorStateStyles.base]: true,
            [errorStateStyles.hover]: !isSelected && isHovered,
            [errorStateStyles.selected]: isSelected,
            [styles.shadow]: isSelected && isRootStep,
        };
    }
    if (hasWarnings) {
        return {
            [warningStateStyles.base]: true,
            [warningStateStyles.hover]: !isSelected && isHovered,
            [warningStateStyles.selected]: isSelected,
            [styles.shadow]: isSelected && isRootStep,
        };
    }
    return {
        [defaultStateStyles.base]: true,
        [defaultStateStyles.hover]: !isSelected && isHovered,
        [defaultStateStyles.selectedStep]: isSelected && !isContainer,
        [defaultStateStyles.selectedStepContainer]: isSelected && isContainer,
        [styles.shadow]: isSelected && isRootStep,
    };
}
function MessagesTooltip({ messages, children }: {
    messages: ReactNode[] | undefined;
    children: ReactNode;
}) {
    return messages && messages.length > 0 ? (<Tooltip className={styles.errorTooltipContainer} display="flex" content={<ProcessSidebarStepMessages messages={messages}/>}>
            {children}
        </Tooltip>) : (<>{children}</>);
}
const headerClassName = "process-sidebar-step-header";
const containerShadowBackgroundColorVariable = "--container-shadow-background-color";
const containerShadowBorderColorVariable = "--container-shadow-border-color";
const styles = {
    root: css({
        position: "relative",
        display: "flex",
        flexGrow: 1,
        minWidth: 0,
        flexDirection: "column",
        borderRadius: borderRadius.small,
        borderWidth: borderWidth[1],
        borderStyle: "solid",
        boxSizing: "border-box",
        transitionProperty: "background-color, border-color",
        transitionDuration: "0.1s",
        [`:has(> .${headerClassName} a:focus-visible)`]: {
            boxShadow: themeTokens.shadow.focused,
        },
    }),
    container: css({
        "&:after": {
            content: "''",
            position: "absolute",
            // absolute positioning doesn't account for border width so need to include it manually
            top: `calc(${space[4]} - ${borderWidth[1]})`,
            left: `calc(${space[4]} - ${borderWidth[1]})`,
            right: `calc(-${space[4]} - ${borderWidth[1]})`,
            bottom: `calc(-${space[4]} - ${borderWidth[1]})`,
            zIndex: -1,
            backgroundColor: `var(${containerShadowBackgroundColorVariable})`,
            borderRadius: borderRadius.small,
            borderColor: `var(${containerShadowBorderColorVariable})`,
            borderWidth: borderWidth[1],
            borderStyle: "solid",
            opacity: 0,
            transitionProperty: "background-color, border-color, opacity",
            transitionDuration: "0.1s",
        },
    }),
    containerCollapsed: css({
        "&:after": {
            opacity: 1,
        },
    }),
    shadow: css({
        boxShadow: themeTokens.shadow.small,
    }),
    header: css({
        display: "flex",
        flexGrow: 1,
        minHeight: "2.625rem",
    }),
    link: css({
        ...resetStyles.anchor,
        display: "flex",
        flex: 1,
        minWidth: 0,
        alignItems: "center",
        gap: space[8],
        "&:hover": {
            color: "inherit", // prevent global link hover styles applying
        },
        padding: `${space[6]} ${space[12]}`,
    }),
    linkWithLeftButton: css({
        paddingLeft: space[4],
    }),
    linkWithRightButton: css({
        paddingRight: space[4],
    }),
    childSteps: css({
        ...resetStyles.list,
        display: "flex",
        flexDirection: "column",
        gap: space[8],
        padding: `0 ${space[12]} ${space[12]} ${space[12]}`,
    }),
    iconContainer: css({
        background: colorScales.white,
        borderRadius: borderRadius.extraSmall,
        alignContent: "center",
        height: "1.5rem",
        width: "1.5rem",
        overflow: "clip",
    }),
    icon: css({
        display: "block",
        objectFit: "scale-down",
        width: "100%",
    }),
    iconDisabled: css({
        opacity: 0.5,
    }),
    name: css({
        flex: 1,
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
        overflow: "clip",
        minWidth: 0,
        font: text.body.regular.small,
        color: themeTokens.color.text.primary,
    }),
    nameSelected: css({
        font: text.body.bold.small,
    }),
    nameDisabled: css({
        textDecoration: "line-through",
        color: themeTokens.color.processStep.text.disabled,
    }),
    errorTooltipContainer: css({
        flex: 1,
        minWidth: 0,
    }),
    expandCollapseButton: css({
        // When tabbing through the sidebar, we should be able to tab to this button
        // Since it only shows when the link is focused (or hovered), it must be placed after the link so that we can tab to it after tabbing to the link
        // But we still visually want the button to appear in front, so this CSS manually sets this order
        order: -1,
        alignSelf: "center",
        marginLeft: space[8],
    }),
    overflowButton: css({
        alignSelf: "center",
        marginRight: space[8],
    }),
    ghostButton: css({
        color: themeTokens.color.processStep.icon.default,
    }),
    ghostButtonError: css({
        color: themeTokens.color.processStep.icon.error.secondary,
    }),
    ghostButtonWarning: css({
        color: themeTokens.color.processStep.icon.warning.secondary,
    }),
};
const defaultStateStyles = {
    base: css({
        backgroundColor: themeTokens.color.processStep.background.primary.default,
        borderColor: themeTokens.color.processStep.border.default,
        [containerShadowBackgroundColorVariable]: themeTokens.color.processStep.background.primary.default,
        [containerShadowBorderColorVariable]: themeTokens.color.processStep.border.default,
    }),
    hover: css({
        backgroundColor: themeTokens.color.processStep.background.hover.primary,
        borderColor: themeTokens.color.processStep.border.hover,
        [containerShadowBackgroundColorVariable]: themeTokens.color.processStep.background.hover.secondary,
        [containerShadowBorderColorVariable]: themeTokens.color.processStep.border.hover,
    }),
    selectedStep: css({
        backgroundColor: themeTokens.color.processStep.background.primary.selected,
        borderColor: themeTokens.color.processStep.border.selected,
        [containerShadowBackgroundColorVariable]: themeTokens.color.processStep.background.hover.secondary,
        [containerShadowBorderColorVariable]: themeTokens.color.processStep.border.hover,
    }),
    selectedStepContainer: css({
        backgroundColor: themeTokens.color.processContainer.background.selected,
        borderColor: themeTokens.color.processContainer.border.selected,
        [containerShadowBackgroundColorVariable]: themeTokens.color.processStep.background.hover.secondary,
        [containerShadowBorderColorVariable]: themeTokens.color.processStep.border.hover,
    }),
};
const warningStateStyles = {
    base: css({
        backgroundColor: themeTokens.color.processStep.background.warning.default,
        borderColor: themeTokens.color.processStep.border.warning.default,
        [containerShadowBackgroundColorVariable]: themeTokens.color.processStep.background.warning.default,
        [containerShadowBorderColorVariable]: themeTokens.color.processStep.border.warning.default,
    }),
    hover: css({
        backgroundColor: themeTokens.color.processStep.background.hover.warning,
        borderColor: themeTokens.color.processStep.border.warning.hover,
        [containerShadowBackgroundColorVariable]: themeTokens.color.processStep.background.warning.secondary,
        [containerShadowBorderColorVariable]: themeTokens.color.processStep.border.warning.hover,
    }),
    selected: css({
        backgroundColor: themeTokens.color.processStep.background.warning.selected,
        borderColor: themeTokens.color.processStep.border.warning.selected,
        [containerShadowBackgroundColorVariable]: themeTokens.color.processStep.background.warning.secondary,
        [containerShadowBorderColorVariable]: themeTokens.color.processStep.border.warning.selected,
    }),
};
const errorStateStyles = {
    base: css({
        backgroundColor: themeTokens.color.processStep.background.error.primary,
        borderColor: themeTokens.color.processStep.border.error.default,
        [containerShadowBackgroundColorVariable]: themeTokens.color.processStep.background.error.primary,
        [containerShadowBorderColorVariable]: themeTokens.color.processStep.border.error.default,
    }),
    hover: css({
        backgroundColor: themeTokens.color.processStep.background.hover.error,
        borderColor: themeTokens.color.processStep.border.error.hover,
        [containerShadowBackgroundColorVariable]: themeTokens.color.processStep.background.error.secondary,
        [containerShadowBorderColorVariable]: themeTokens.color.processStep.border.error.hover,
    }),
    selected: css({
        backgroundColor: themeTokens.color.processStep.background.error.selected,
        borderColor: themeTokens.color.processStep.border.error.selected,
        [containerShadowBackgroundColorVariable]: themeTokens.color.processStep.background.error.secondary,
        [containerShadowBorderColorVariable]: themeTokens.color.processStep.border.error.selected,
    }),
};
